import React from 'react';
import { Button } from 'reactstrap';
import { SEARCH_PLACEHOLDER, SEARCH_NOT_FOUND } from '../../utils/locationPin';
import { SUGGEST_DELAY_MS, suggestionRows, nextHighlight, placeGeometry, shouldSuggest } from '../../utils/placesSearch';
import './placeSearch.scss';

// The search box above a location map. While typing, Google's Places API (New) suggests places (our own list under the
// box); picking one calls onPlace(geometry, address). Find, or Enter with no suggestion highlighted, looks the typed
// text up with the geocoder instead, which is also all the box does once Places has failed (not enabled for the key, no
// network): the failure is logged once and the box carries on without suggestions.
//   <PlaceSearch google={google} map={map} onPlace={(geometry, address) => ...} />
// geometry is { location: LatLng, viewport: LatLngBounds or null }, as in a geocoder answer.
class PlaceSearch extends React.Component {
	constructor(props) {
		super(props);
		this.state = { text: '', rows: [], highlight: -1, message: '' };
		this.asked = 0;
		this.placesOff = false;
	}

	componentWillUnmount() {
		this.unmounted = true;
		clearTimeout(this.timer);
	}

	places() {
		const { google } = this.props;
		if (this.placesOff || !google || !google.maps || typeof google.maps.importLibrary !== 'function') {
			return Promise.resolve(null);
		}
		if (!this.placesLibrary) {
			this.placesLibrary = google.maps.importLibrary('places').catch(() => null);
		}
		return this.placesLibrary;
	}

	placesFailed(error) {
		if (!this.placesOff) {
			// Shown in the browser console only: the box still finds places with the geocoder
			console.warn('Map search suggestions are off: ' + (error && error.message ? error.message : error));
		}
		this.placesOff = true;
		this.setState({ rows: [], highlight: -1 });
	}

	typed = (event) => {
		const text = event.target.value;
		this.setState({ text, message: '', highlight: -1 });
		clearTimeout(this.timer);
		if (!shouldSuggest(text) || this.placesOff) {
			this.asked++;
			this.setState({ rows: [] });
			return;
		}
		this.timer = setTimeout(() => this.suggest(text), SUGGEST_DELAY_MS);
	};

	suggest(text) {
		const asked = ++this.asked;
		this.places().then((places) => {
			if (!places || !places.AutocompleteSuggestion) {
				this.placesFailed('the Places library did not load');
				return null;
			}
			if (!this.sessionToken) {
				this.sessionToken = new places.AutocompleteSessionToken();
			}
			const request = { input: text, sessionToken: this.sessionToken };
			const bounds = this.props.map && typeof this.props.map.getBounds === 'function' ? this.props.map.getBounds() : null;
			if (bounds) {
				request.locationBias = bounds;
			}
			return places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then((response) => {
				// Only the answer to the latest text
				if (!this.unmounted && asked === this.asked) {
					this.setState({ rows: suggestionRows(response), highlight: -1 });
				}
			});
		}).catch((error) => {
			if (!this.unmounted) {
				this.placesFailed(error);
			}
		});
	}

	pick(row) {
		this.asked++;
		this.setState({ rows: [], highlight: -1, text: row.secondary ? row.main + ', ' + row.secondary : row.main });
		// The session ends with the place's details
		this.sessionToken = null;
		Promise.resolve()
			.then(() => {
				const place = row.prediction.toPlace();
				return place.fetchFields({ fields: ['location', 'viewport', 'formattedAddress'] }).then(() => place);
			})
			.then((place) => {
				const geometry = placeGeometry(place);
				if (this.unmounted) {
					return;
				}
				if (geometry) {
					this.props.onPlace(geometry, place.formattedAddress);
				} else {
					this.find();
				}
			})
			.catch((error) => {
				if (!this.unmounted) {
					this.placesFailed(error);
					this.find();
				}
			});
	}

	// The geocoder: Find, Enter, or a suggestion that could not be opened
	find = () => {
		const text = this.state.text.trim();
		const { google } = this.props;
		this.asked++;
		this.setState({ rows: [], highlight: -1 });
		if (!text || !google) {
			return;
		}
		new google.maps.Geocoder().geocode({ address: text }, (results, status) => {
			if (this.unmounted) {
				return;
			}
			if (status !== 'OK' || !results || !results[0]) {
				this.setState({ message: SEARCH_NOT_FOUND });
				return;
			}
			this.props.onPlace(results[0].geometry, results[0].formatted_address);
		});
	};

	keyDown = (event) => {
		const { rows, highlight } = this.state;
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			if (rows.length) {
				event.preventDefault();
				this.setState({ highlight: nextHighlight(highlight, event.key, rows.length) });
			}
		} else if (event.key === 'Enter') {
			event.preventDefault();
			if (highlight >= 0 && rows[highlight]) {
				this.pick(rows[highlight]);
			} else {
				this.find();
			}
		} else if (event.key === 'Escape') {
			this.asked++;
			this.setState({ rows: [], highlight: -1 });
		}
	};

	render() {
		const { text, rows, highlight, message } = this.state;
		return (
			<div className="place-search">
				<div className="location-search">
					<div className="place-search-box">
						<input type="text" className="location-text-input" placeholder={SEARCH_PLACEHOLDER} aria-label={SEARCH_PLACEHOLDER}
							value={text} onChange={this.typed} onKeyDown={this.keyDown} autoComplete="off"
							role="combobox" aria-expanded={rows.length > 0} aria-controls="place-search-list" aria-autocomplete="list"
							onBlur={() => setTimeout(() => !this.unmounted && this.setState({ rows: [], highlight: -1 }), 200)} />
						{rows.length > 0 && (
							<ul className="place-search-list" id="place-search-list" role="listbox">
								{rows.map((row, index) => (
									<li key={row.key} role="option" aria-selected={index === highlight}
										className={'place-search-row' + (index === highlight ? ' place-search-row-on' : '')}
										onMouseDown={(event) => { event.preventDefault(); this.pick(row); }}
										onMouseEnter={() => this.setState({ highlight: index })}>
										<span className="place-search-main">{row.main}</span>
										{row.secondary ? <span className="place-search-secondary">{row.secondary}</span> : null}
									</li>
								))}
								<li className="place-search-credit" aria-hidden="true">Suggestions by Google</li>
							</ul>
						)}
					</div>
					<Button type="button" color="primary" onClick={this.find}>Find</Button>
				</div>
				{message ? <p className="location-search-message">{message}</p> : null}
			</div>
		);
	}
}

export default PlaceSearch;
